import React, { useState, useEffect } from 'react';
import { FiZap, FiPlus, FiTrash2, FiToggleLeft, FiToggleRight, FiX, FiArrowRight } from 'react-icons/fi';
import { useAuth } from '../../../hooks/useAuth';
import { useTask } from '../contexts/TaskContext';
// SUPABASE SWITCH: revert to '../services/firestore' for Firebase
import { createRule, deleteRule, toggleRule, subscribeToRules } from '../services/firestore';
import toast from 'react-hot-toast';

const TRIGGERS = [
  { key: 'status_change', label: 'Estado cambia a', values: [
    { key: 'planificacion', label: 'Planificacion' },
    { key: 'en-curso', label: 'En Curso' },
    { key: 'finalizado', label: 'Finalizado' },
  ]},
  { key: 'priority_change', label: 'Prioridad cambia a', values: [
    { key: 'alta', label: 'Alta' },
    { key: 'media', label: 'Media' },
    { key: 'baja', label: 'Baja' },
  ]},
  { key: 'assigned', label: 'Tarea es asignada', values: [] },
  { key: 'any_update', label: 'Cualquier actualizacion', values: [] },
];

const ACTIONS = [
  { key: 'set_status', label: 'Cambiar estado a', values: [
    { key: 'planificacion', label: 'Planificacion' },
    { key: 'en-curso', label: 'En Curso' },
    { key: 'finalizado', label: 'Finalizado' },
  ]},
  { key: 'set_priority', label: 'Cambiar prioridad a', values: [
    { key: 'alta', label: 'Alta' },
    { key: 'media', label: 'Media' },
    { key: 'baja', label: 'Baja' },
  ]},
  { key: 'move_section', label: 'Mover a seccion', values: [] },
  { key: 'assign_to', label: 'Asignar a', values: [] },
];

const AutomationRules = ({ onClose }) => {
  const { user } = useAuth();
  const { selectedProjectId, selectedFolderId, selectedListId, sections, users } = useTask();
  const [rules, setRules] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newRule, setNewRule] = useState({
    name: '',
    trigger: 'status_change',
    triggerValue: 'finalizado',
    action: 'set_status',
    actionValue: 'en-curso',
  });

  useEffect(() => {
    if (!user?.uid || !selectedProjectId || !selectedFolderId || !selectedListId) return;
    const unsub = subscribeToRules(user.uid, selectedProjectId, selectedFolderId, selectedListId, setRules);
    return () => unsub();
  }, [user?.uid, selectedProjectId, selectedFolderId, selectedListId]);

  const selectedTrigger = TRIGGERS.find(t => t.key === newRule.trigger);
  const selectedAction = ACTIONS.find(a => a.key === newRule.action);

  const getActionValues = (actionKey) => {
    const action = ACTIONS.find(a => a.key === actionKey);
    if (actionKey === 'move_section') {
      return sections.map(s => ({ key: s.id, label: s.nombre }));
    }
    if (actionKey === 'assign_to') {
      return users.map(u => ({ key: u.email, label: u.displayName || u.email }));
    }
    return action?.values || [];
  };

  const handleCreate = async () => {
    if (!newRule.name.trim()) {
      toast.error('Nombre requerido');
      return;
    }
    try {
      await createRule(user.uid, selectedProjectId, selectedFolderId, selectedListId, newRule);
      toast.success('Regla creada');
      setShowNew(false);
      setNewRule({ name: '', trigger: 'status_change', triggerValue: 'finalizado', action: 'set_status', actionValue: 'en-curso' });
    } catch {
      toast.error('Error al crear regla');
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Eliminar esta regla?')) return;
    try {
      await deleteRule(user.uid, selectedProjectId, selectedFolderId, selectedListId, ruleId);
      toast.success('Regla eliminada');
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleToggle = async (ruleId, enabled) => {
    try {
      await toggleRule(user.uid, selectedProjectId, selectedFolderId, selectedListId, ruleId, !enabled);
    } catch {
      toast.error('Error');
    }
  };

  const getTriggerLabel = (rule) => {
    const t = TRIGGERS.find(x => x.key === rule.trigger);
    const v = t?.values?.find(x => x.key === rule.triggerValue);
    return `${t?.label || rule.trigger}${v ? `: ${v.label}` : ''}`;
  };

  const getActionLabel = (rule) => {
    const a = ACTIONS.find(x => x.key === rule.action);
    let valueLabel = rule.actionValue;
    if (rule.action === 'move_section') {
      const sec = sections.find(s => s.id === rule.actionValue);
      valueLabel = sec?.nombre || rule.actionValue;
    } else if (rule.action === 'assign_to') {
      const u = users.find(x => x.email === rule.actionValue);
      valueLabel = u?.displayName || rule.actionValue;
    } else {
      const v = a?.values?.find(x => x.key === rule.actionValue);
      valueLabel = v?.label || rule.actionValue;
    }
    return `${a?.label || rule.action}: ${valueLabel}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-lg max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <FiZap size={18} className="text-amber-500" />
            <h2 className="font-bold text-gray-800 dark:text-gray-100">Automatizaciones</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400">
            <FiX size={18} />
          </button>
        </div>

        {/* Rules List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {rules.length === 0 && !showNew && (
            <div className="text-center py-8">
              <FiZap size={32} className="text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No hay automatizaciones</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Crea reglas para automatizar acciones en tus tareas</p>
            </div>
          )}

          {rules.map(rule => (
            <div key={rule.id} className={`p-3 rounded-lg border transition-colors ${rule.enabled ? 'bg-amber-50/50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800/30' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-60'}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{rule.name}</span>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => handleToggle(rule.id, rule.enabled)} className="p-1 text-gray-400 hover:text-amber-600 transition-colors">
                    {rule.enabled ? <FiToggleRight size={18} className="text-amber-500" /> : <FiToggleLeft size={18} />}
                  </button>
                  <button onClick={() => handleDelete(rule.id)} className="p-1 text-gray-400 hover:text-red-500 transition-colors">
                    <FiTrash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded font-medium">
                  Cuando: {getTriggerLabel(rule)}
                </span>
                <FiArrowRight size={12} className="text-gray-400" />
                <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded font-medium">
                  {getActionLabel(rule)}
                </span>
              </div>
            </div>
          ))}

          {/* New Rule Form */}
          {showNew && (
            <div className="p-4 rounded-lg border-2 border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/30 dark:bg-amber-900/10 space-y-3">
              <input
                type="text"
                value={newRule.name}
                onChange={e => setNewRule(p => ({ ...p, name: e.target.value }))}
                placeholder="Nombre de la regla..."
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-400"
                autoFocus
              />

              {/* Trigger */}
              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Cuando</label>
                <select
                  value={newRule.trigger}
                  onChange={e => setNewRule(p => ({ ...p, trigger: e.target.value, triggerValue: TRIGGERS.find(t => t.key === e.target.value)?.values?.[0]?.key || '' }))}
                  className="w-full mt-1 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                >
                  {TRIGGERS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
                {selectedTrigger?.values?.length > 0 && (
                  <select
                    value={newRule.triggerValue}
                    onChange={e => setNewRule(p => ({ ...p, triggerValue: e.target.value }))}
                    className="w-full mt-1 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                  >
                    {selectedTrigger.values.map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
                  </select>
                )}
              </div>

              {/* Action */}
              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Entonces</label>
                <select
                  value={newRule.action}
                  onChange={e => {
                    const vals = getActionValues(e.target.value);
                    setNewRule(p => ({ ...p, action: e.target.value, actionValue: vals[0]?.key || '' }));
                  }}
                  className="w-full mt-1 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                >
                  {ACTIONS.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
                </select>
                {getActionValues(newRule.action).length > 0 && (
                  <select
                    value={newRule.actionValue}
                    onChange={e => setNewRule(p => ({ ...p, actionValue: e.target.value }))}
                    className="w-full mt-1 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                  >
                    {getActionValues(newRule.action).map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
                  </select>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <button onClick={() => setShowNew(false)} className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                  Cancelar
                </button>
                <button onClick={handleCreate} className="px-3 py-1.5 text-xs bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-medium">
                  Crear Regla
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!showNew && (
          <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800">
            <button
              onClick={() => setShowNew(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
            >
              <FiPlus size={16} /> Nueva Automatizacion
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutomationRules;
